<template>
  <div
    class="tw-sticky tw-top-0 tw-bg-sf-snow-lighter tw-shadow-sidepane tw-z-10"
  >
    <SfHeightWatcher :variable-name="'--communication-sidepane-header-height'">
      <div
        ref="outerContainerRef"
        class="
          tw-flex
          tw-items-center
          tw-justify-between
          tw-w-full
          tw-px-6
          tw-border
          tw-border-t-0
          tw-border-r-0
          tw-border-l-0
          tw-border-solid
          tw-border-sf-neutral-lighter-1
          tw-h-[60px]
        "
      >
        <div
          ref="iconContainerRef"
          class="
            tw-flex-1
            tw-h-full
            tw-flex
            tw-items-stretch
            tw-justify-between
            tw-pr-2
          "
        >
          <HeaderItem
            :disabled="
              allNotesCount === 0 &&
              allTodosCount === 0 &&
              totalChecks === 0 &&
              allChecksCount === 0
            "
            icon="comments"
            :only-tooltip="onlyTooltips"
            :selected="typeFilter === 'all' || typeFilter === null"
            :text="I18n.t('communication.sidepane_header.all')"
            type="all"
            @click="handleItemClick"
          />
          <HeaderItem
            :disabled="allNotesCount === 0"
            icon="comment"
            :only-tooltip="onlyTooltips"
            :text="I18n.t('communication.sidepane_header.notes')"
            :selected="typeFilter === 'note'"
            :show-new="unreadNotesCount > 0"
            type="note"
            @click="handleItemClick"
          />
          <HeaderItem
            :disabled="allTodosCount === 0"
            icon="bolt"
            :only-tooltip="onlyTooltips"
            :new-item-count="pendingTodosCount"
            :selected="typeFilter === 'todo'"
            :show-new="unreadTodosCount > 0"
            :text="I18n.t('communication.sidepane_header.todos')"
            type="todo"
            @click="handleItemClick"
          />
          <HeaderItem
            v-if="canShowAssistantChecks && totalChecks > 0"
            icon="wand-magic-sparkles"
            :only-tooltip="onlyTooltips"
            :selected="typeFilter === 'assistant'"
            :show-new="unreadChecks.length > 0"
            :text="I18n.t('communication.sidepane_header.assistant')"
            type="assistant"
            ai
            @click="handleItemClick"
          />
          <HeaderItem
            v-if="allChecksCount > 0"
            icon="check-square"
            :only-tooltip="onlyTooltips"
            :new-item-count="pendingChecksCount"
            :selected="typeFilter === 'checklist'"
            :text="I18n.t('communication.sidepane_header.checks')"
            type="checklist"
            @click="handleItemClick"
          />
        </div>
        <div ref="backIconContainerRef">
          <SfButtonDs
            v-tooltip.html.placementLeft="closeTooltip"
            variant="tertiary"
            icon-only
            icon-before="arrow-right"
            :aria-label="I18n.t('common.close')"
            @click="closeRightDrawer"
          />
        </div>
      </div>
      <slot name="before-new-button"></slot>
      <div
        v-if="canAddMessage !== false"
        class="
          tw-px-6
          tw-py-3
          tw-border
          tw-border-t-0
          tw-border-r-0
          tw-border-l-0
          tw-border-solid
          tw-border-sf-neutral-lighter-1
        "
      >
        <NewRemarkButton
          extra-classes="sf-full sf-alignc"
          :ledger-id="ledgerId"
          :ledger-label="ledgerLabel"
          :owner-id="ownerId"
          :owner-type="ownerType"
          :selected-tag-ids="talkingPointTagIds"
          :is-client-meeting-remark="lockTalkingPointTag"
        ></NewRemarkButton>
      </div>
    </SfHeightWatcher>
  </div>
</template>

<script setup lang="ts">
import { useElementSize } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, ref, watchEffect } from 'vue'

import {
  getRightDrawerHotkey,
  getHotkeyKbd,
} from '@app/application/behaviors/drawers/hotkeys'
import { useTalkingPointTag } from '@app/application/client_meetings/composables/use-talking-point-tag'
import HeaderItem from './sf-sidepane-header-item.vue'
import useI18n from '../../composables/use-I18n'
import useCommsHeaderItemCounts from '@app/application/communication/sections/communication/composables/use-comms-header-item-data'
import { showSfAssistantChecks } from '@app/application/sf-assistant-checks/utils'
import NewRemarkButton from '@app/shared/vue/components/new-remark/new-remark-button.vue'
import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import SfHeightWatcher from '@app/shared/vue/components/sf-height-watcher.vue'

import { useChecksStore } from '@app/application/sf-assistant-checks/stores/checks'
import { isUsingNewLayout } from '@app/adapters/silverfin'
import { useCommsFilterStore } from '@app/application/communication/store/comms-filters'
import { useDrawer } from '@app/shared/pinia/drawer'

interface Props {
  ledgerLabel: string
  ownerId?: number
  ownerType?: string
  canAddMessage: boolean
}

withDefaults(defineProps<Props>(), {
  ownerId: undefined,
  ownerType: undefined,
  canAddMessage: true,
})

const assistantChecksStore = useChecksStore()
const { unreadChecks, totalChecks } = storeToRefs(assistantChecksStore)

const drawerStore = useDrawer()
const { closeRightDrawer } = drawerStore

const filterStore = useCommsFilterStore()
const { typeFilter } = storeToRefs(filterStore)

const I18n = useI18n()

const kbdHtml = getHotkeyKbd(getRightDrawerHotkey())
const usingNewLayout = isUsingNewLayout()

const closeTooltip = usingNewLayout
  ? `${I18n.t('navigation.right_drawer.close_messages')} ${kbdHtml}`
  : ''

const outerContainerRef = ref<HTMLElement | null>(null)
const iconContainerRef = ref<HTMLElement | null>(null)
const backIconContainerRef = ref<HTMLElement | null>(null)

const onlyTooltips = ref(false)

const { width: iconContainerWidth } = useElementSize(iconContainerRef)

const {
  allChecksCount,
  allNotesCount,
  allTodosCount,
  handleItemClick,
  pendingChecksCount,
  pendingTodosCount,
  unreadNotesCount,
  unreadTodosCount,
} = useCommsHeaderItemCounts()

const ledgerId = window.Silverfin.currentLedgerId()

const canShowAssistantChecks = showSfAssistantChecks()

// In presentation mode a message added from the comms drawer must be a talking
// point, so preselect and lock the talking-point tag — mirroring the present-screen
// header. Outside presentation mode the drawer stays a normal remark form.
const presentationMode = window.Silverfin.presentationMode
const { talkingPointTag, talkingPointTagIds: storeTalkingPointTagIds } =
  useTalkingPointTag()
const talkingPointTagIds = computed(() =>
  presentationMode ? storeTalkingPointTagIds.value : undefined
)
const lockTalkingPointTag = computed(
  () => presentationMode && !!talkingPointTag.value
)

/**
 * There are situations where the icon translations are too long to fit in the container for certain languages.
 * In this case, we only show the icons and display the text in a tooltip.
 */
watchEffect(() => {
  if (
    iconContainerWidth.value &&
    outerContainerRef.value &&
    backIconContainerRef.value
  ) {
    const outerWidth = outerContainerRef.value.offsetWidth
    const backIconWidth = backIconContainerRef.value.offsetWidth

    if (outerWidth < iconContainerWidth.value + backIconWidth) {
      onlyTooltips.value = true
    }
  }
})
</script>
